import { ensureProgramOccurrenceId } from '@/components/programs/ensure-occurrence';
import { api } from '@/lib/api';

type RoleKind =
  | 'preacher'
  | 'liturgist'
  | 'service_leader'
  | 'bible_reader'
  | 'communion'
  | 'other';

interface WorshipAssignment {
  id: string;
  memberId: string;
  roleKind: RoleKind | null;
}

interface WorshipPayload {
  assignments: WorshipAssignment[];
}

export interface WorshipDraft {
  preacherMemberId: string | null;
  includesCommunion: boolean;
  assignments: Array<{
    memberId: string;
    roleKind: RoleKind;
  }>;
}

const assignmentKey = (memberId: string, roleKind: RoleKind | null) =>
  `${memberId}:${roleKind ?? 'other'}`;

export const applyWorshipDraft = async (input: {
  programId: string;
  dates: string[];
  draft: WorshipDraft;
}) => {
  const planDates = [...new Set(input.dates)].sort();
  if (planDates.length === 0) return;

  const occurrenceId = await ensureProgramOccurrenceId({
    programId: input.programId,
    occurrenceDate: planDates[0]!,
  });
  const applyTo = planDates.length > 1 ? { applyToOccurrenceDates: planDates } : {};

  await api.patch(`/programs/occurrences/${occurrenceId}/worship`, {
    preacherMemberId: input.draft.preacherMemberId,
    includesCommunion: input.draft.includesCommunion,
    ...applyTo,
  });

  const current = await api.get<WorshipPayload>(
    `/programs/occurrences/${occurrenceId}/worship`,
  );
  const existingNonPreacher = current.assignments.filter(
    (assignment) => assignment.roleKind !== 'preacher',
  );

  const desiredKeys = new Set(
    input.draft.assignments.map((assignment) =>
      assignmentKey(assignment.memberId, assignment.roleKind),
    ),
  );
  const existingKeys = new Set(
    existingNonPreacher.map((assignment) =>
      assignmentKey(assignment.memberId, assignment.roleKind),
    ),
  );

  for (const assignment of existingNonPreacher) {
    const key = assignmentKey(assignment.memberId, assignment.roleKind);
    if (!desiredKeys.has(key)) {
      await api.delete(
        `/programs/occurrences/${occurrenceId}/worship/assignments/${assignment.id}`,
        { body: applyTo },
      );
    }
  }

  for (const assignment of input.draft.assignments) {
    const key = assignmentKey(assignment.memberId, assignment.roleKind);
    if (!existingKeys.has(key)) {
      await api.post(`/programs/occurrences/${occurrenceId}/worship/assignments`, {
        memberId: assignment.memberId,
        roleKind: assignment.roleKind,
        ...applyTo,
      });
    }
  }
};
